import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, of, switchMap, throwError } from "rxjs";
import { environment } from "../../../environments/environment";
import { ApiSettingsUtils, AuthSettingsUtils } from "../configuration/configuration.types";
import { ACCESS_TOKEN, JWT_PAGE_TYPE_CLAIM, JWT_SHORT_CODE_CLAIM } from "./auth.constants";
import { PageType, TokenResponse } from "./auth.types";
import { getJwtClaim, isJwtTokenExpired } from "./jwt.utils";

@Injectable({
    providedIn: "root",
})
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem(ACCESS_TOKEN, token);
    }

    get accessToken(): string {
        return localStorage.getItem(ACCESS_TOKEN) ?? '';
    }

    /**
     * Sign in
     *
     * @param credentials - Must include email OR phone, and captchaToken
     */
    signIn(credentials: { email: string; phoneNumber: string; captchaToken: string }): Observable<any> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        if (!credentials.captchaToken) {
            return throwError(() => new Error('CAPTCHA token is required.'));
        }

        if (!credentials.email && !credentials.phoneNumber) {
            return throwError(() => new Error('Either email or phone is required.'));
        }

        return this._httpClient.post<TokenResponse>(AuthSettingsUtils.getLoginUrl(environment.authSettings), credentials).pipe(
            switchMap((response: TokenResponse) =>
            {
                this.authenticateUser(response);

                sessionStorage.setItem('verified', 'true');

                return of(response);
            }),
        );
    }

    verifyOneTimeCode(shortLink: string, oneTimeCode: string): Observable<any> {
        return this._httpClient
            .post<TokenResponse>(
                `${ApiSettingsUtils.getBaseApiUrl(environment.apiSettings)}/authentication/${shortLink}/${oneTimeCode}`,
                null
            ).pipe(
                switchMap((response: TokenResponse) =>
                {
                    this.authenticateUser(response);

                    sessionStorage.setItem('verified', 'true');

                    return of(response);
                }),
        );
    }

    private authenticateUser(tokenResponse: TokenResponse): void {
        this._authenticated = true;

        this.updateUser(tokenResponse);
    }

    private loginFromToken(): void {
        this.updateUser({
            accessToken: this.accessToken,
        } as TokenResponse);
        sessionStorage.setItem('verified', 'true');
    }

    public updateUser(tokenResponse: TokenResponse): void {
        this.accessToken = tokenResponse.accessToken;

        this._authenticated = true;
    }

    /**
     * Check if user is authenticated
     * @returns true if user is authenticated and token is not expired
     */
    isAuthenticated(): boolean {
        const token = this.accessToken;

        if (!token) {
            this._authenticated = false;
            return false;
        }

        if (isJwtTokenExpired(token)) {
            this._authenticated = false;
            return false;
        }

        if (!this._authenticated) {
            this.loginFromToken();
        }

        return this._authenticated;
    }

    /**
     * Get PageType from JWT token
     * @returns PageType or null if not found
     */
    getPageType(): PageType | null {
        const token = this.accessToken;

        if (!token) {
            return null;
        }

        const pageTypeValue = getJwtClaim(token, JWT_PAGE_TYPE_CLAIM);

        if (pageTypeValue && Object.values(PageType).includes(pageTypeValue)) {
            return pageTypeValue as PageType;
        }

        return null;
    }

    getShortCode(): string | null {
        return this.getClaimValue(JWT_SHORT_CODE_CLAIM);
    }

    /**
     * Sign out
     */
    signOut(): void {
        localStorage.removeItem(ACCESS_TOKEN);

        this._authenticated = false;
    }

    private getClaimValue(claimName: string) {
        const token = this.accessToken;

        if (!token) {
            return null;
        }

        return getJwtClaim(token, claimName);
    }
}
